import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, Target, BookOpen, Heart } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { SectionHeading } from '../components/ui/SectionHeading';
import { FormationCard } from '../components/FormationCard';
import { QualiopiTeaser } from '../components/QualiopiTeaser';
import { ContactCTA } from '../components/ContactCTA';
import { Temoignages } from '../components/Temoignages';
import { useSeo } from '../hooks/useSeo';
import { SocialProof } from '../components/SocialProof';
import { Cap360 } from '../components/Cap360';
import { FORMATIONS, RENTREE } from '../data/constants';
import { OFFRES } from '../data/offres';
import { Badge } from '../components/ui/Badge';
export function Home() {
  useSeo({
    title: 'CFA en alternance à Joigny (89) — Commerce, vente, relation client',
    description: "Zillenium Alternance, CFA certifié Qualiopi à Joigny dans l'Yonne. BTS NDRC, TP Conseiller de Vente et TP Négociateur Technico-Commercial en apprentissage. Offres disponibles, démarrage immédiat possible en entreprise, rentrée en formation le 25 septembre 2026.",
    path: '/'
  });
  return <div className="flex flex-col w-full" data-id="element-1976" data-mp="2r0g8je9lulfa">
      {/* Hero Section */}
      <section className="relative bg-lavande pt-24 pb-40 overflow-hidden" data-id="element-1977" data-mp="2mwqpf4bp8w1n">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-multiply" data-id="element-1978" data-mp="2hyyvk4rhuzut"></div>
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[600px] h-[600px] bg-primary rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1979" data-mp="288vv5ji1mjmg" />
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1980" data-mp="1z7xo0scb5vzn" />

        <div className="container relative z-10 text-center max-w-4xl mx-auto" data-id="element-1981" data-mp="1kkqodis9wkg2">
          <Badge className="mb-8 bg-white text-brand-orange border-none shadow-sm text-sm px-5 py-2 font-bold uppercase tracking-widest" data-id="element-1982" data-mp="1kmtnqtfbfpxg">
            Centre de Formation d'Apprentis à Joigny
          </Badge>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold text-primary-dark mb-8 tracking-tight leading-[1.1]" data-id="element-1983" data-mp="1aow0o996zz6f">
            Révélez votre potentiel.
            <br data-id="element-1984" data-mp="23c07mjruinku" />
            <span className="text-primary" data-id="element-1985" data-mp="23dktll03gy73">Construisez votre avenir.</span>
          </h1>
          <p className="text-xl md:text-2xl text-primary-dark/80 mb-12 max-w-2xl mx-auto leading-relaxed font-medium" data-id="element-1986" data-mp="28ce5j8zqgh55">
            L'alternance avec Zillenium : une pédagogie par l'action, un
            accompagnement sur-mesure et un tremplin direct vers l'emploi.
          </p>
          <Button size="lg" className="group/btn bg-primary hover:bg-primary-dark text-white text-lg px-8 py-7 shadow-xl" asChild data-id="element-1987" data-mp="2m8hzr2alwphm">
            <Link to="/le-cfa" data-id="element-1988" data-mp="fksgo5u101md">
              Découvrir le CFA
              <ArrowRight className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" data-id="element-1989" data-mp="amz9b9gzl1ki" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Dual Entry Block (Overlapping Hero) */}
      <section className="container relative z-20 -mt-24 mb-32" data-id="element-1990" data-mp="kcafan8ktq46">
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-1991" data-mp="kewpkumuqjq3">
          {/* Candidat Card */}
          <Link to="/candidater" className="group relative block outline-none" data-id="element-1992" data-mp="3nt94fbqvk4pi">
            <div className="absolute inset-0 bg-brand-orange/5 rounded-[2.5rem] transform transition-transform duration-500 group-hover:scale-[1.02] group-hover:bg-brand-orange/10" data-id="element-1993" data-mp="3nvc5cqtkfi90" />
            <Card className="relative h-full border-none shadow-xl hover:shadow-2xl transition-all duration-500 rounded-[2.5rem] bg-white/90 backdrop-blur-sm overflow-hidden" data-id="element-1994" data-mp="n03hkbybpbc">
              <CardContent className="p-10 md:p-12 flex flex-col h-full" data-id="element-1995" data-mp="1c2oq2alammbm">
                <div className="flex justify-between items-start mb-8" data-id="element-1996" data-mp="27eeou7ckyvgc">
                  <div className="w-16 h-16 bg-brand-orange/10 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3" data-id="element-1997" data-mp="27g9cvuj5f3on">
                    <Users className="w-8 h-8 text-brand-orange" data-id="element-1998" data-mp="2he5n8k49hulp" />
                  </div>
                  <div className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center transition-all duration-300 group-hover:bg-brand-orange group-hover:border-brand-orange group-hover:text-white text-slate-400" data-id="element-1999" data-mp="2mcgvwritlz2c">
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-45" data-id="element-2000" data-mp="3020s1ehwby2o" />
                  </div>
                </div>
                <h2 className="text-3xl font-extrabold font-heading mb-4 text-primary-dark transition-colors group-hover:text-brand-orange" data-id="element-2001" data-mp="350vin2m3uqxc">
                  Je suis candidat
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed flex-1" data-id="element-2002" data-mp="3e2la28fzlpv4">
                  Trouvez votre voie, formez-vous à un métier d'avenir et
                  obtenez un diplôme d'État ou un titre professionnel reconnu,
                  tout en étant rémunéré.
                </p>
                <div className="mt-8 font-bold text-brand-orange flex items-center" data-id="element-2003" data-mp="tejxx8apq5yz">
                  Candidater maintenant
                  <div className="h-px bg-brand-orange flex-1 ml-4 opacity-0 transition-opacity duration-300 group-hover:opacity-30" data-id="element-2004" data-mp="1m2zbdqe74g87" />
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Entreprise Card */}
          <Link to="/entreprises" className="group relative block outline-none" data-id="element-2005" data-mp="1cbuweqoisvdd">
            <div className="absolute inset-0 bg-primary/20 rounded-[2.5rem] transform transition-transform duration-500 group-hover:scale-[1.02] group-hover:bg-primary/30" data-id="element-2006" data-mp="17euo09477gj3" />
            <Card className="relative h-full border-none shadow-xl hover:shadow-2xl transition-all duration-500 rounded-[2.5rem] bg-primary-dark overflow-hidden" data-id="element-2007" data-mp="2030d182abroq">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary rounded-full blur-[80px] opacity-20 transition-opacity duration-500 group-hover:opacity-40 pointer-events-none" data-id="element-2008" data-mp="3ubdungdo7nw5" />
              <CardContent className="p-10 md:p-12 flex flex-col h-full relative z-10" data-id="element-2009" data-mp="3kk9h8l3n8b3f">
                <div className="flex justify-between items-start mb-8" data-id="element-2010" data-mp="3pikr2vtxulln">
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3" data-id="element-2011" data-mp="3ambvq5d64sei">
                    <Target className="w-8 h-8 text-lavande" data-id="element-2012" data-mp="31m6mrqsbtly8" />
                  </div>
                  <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:border-white group-hover:text-primary-dark text-white/50" data-id="element-2013" data-mp="36l02zqyzzr23">
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-45" data-id="element-2014" data-mp="14twt53wxmgil" />
                  </div>
                </div>
                <h2 className="text-3xl font-extrabold font-heading mb-4 text-white transition-colors group-hover:text-lavande" data-id="element-2015" data-mp="1ev7ge82kqdlx">
                  Je suis une entreprise
                </h2>
                <p className="text-lavande text-lg leading-relaxed flex-1" data-id="element-2016" data-mp="1nrzxd2cahylk">
                  Recrutez vos futurs talents, formez-les à vos méthodes et
                  bénéficiez d'un accompagnement complet dans vos démarches.
                </p>
                <div className="mt-8 font-bold text-white flex items-center" data-id="element-2017" data-mp="1sprpnxgujmqa">
                  Recruter un alternant
                  <div className="h-px bg-white flex-1 ml-4 opacity-0 transition-opacity duration-300 group-hover:opacity-30" data-id="element-2018" data-mp="26ngbp8mngzwx" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Pourquoi choisir Zillenium */}
      <section className="py-24 bg-muted relative overflow-hidden" data-id="element-2019" data-mp="89wtvxiuabwn">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-2020" data-mp="wu31qjgzia34" />
        <div className="container relative z-10" data-id="element-2021" data-mp="11s4opfhj6tjv">
          <SectionHeading title="Pourquoi choisir Zillenium Alternance" subtitle="Un CFA à taille humaine, engagé pour la réussite de ses apprentis et la satisfaction de ses entreprises partenaires." centered data-id="element-2022" data-mp="16jv0lpw49pde" />

          <div className="grid md:grid-cols-3 gap-12 mt-20" data-id="element-2023" data-mp="dwim83040nv6">
            <div className="text-center group" data-id="element-2024" data-mp="itsahfbrithf">
              <div className="w-20 h-20 bg-white shadow-md rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-2025" data-mp="iuk8sxwqv5be">
                <Users className="w-10 h-10 text-primary animate-soft-bounce" data-id="element-2026" data-mp="rvrrw8qovzkw" />
              </div>
              <h3 className="text-2xl font-bold font-heading mb-4 text-primary-dark" data-id="element-2027" data-mp="1ya9a79x3kfj9">
                Accompagnement individualisé
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed mb-4" data-id="element-2028" data-mp="2de2jj6vye1ym">
                Des effectifs réduits pour un suivi personnalisé, de la
                recherche d'entreprise jusqu'à l'obtention du diplôme.
              </p>
              <Link to="/candidater#cap-360-section" className="inline-flex items-center text-brand-orange font-bold hover:text-primary transition-colors" data-id="element-2029" data-mp="23n7p3f8jzw23">
                Découvrir CAP 360 <ArrowRight className="w-4 h-4 ml-1" data-id="element-2030" data-mp="1uovz9ffih2ap" />
              </Link>
            </div>
            <div className="text-center group" data-id="element-2031" data-mp="1pmwvi4fv03d8">
              <div className="w-20 h-20 bg-white shadow-md rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-2032" data-mp="1kp51n4vnm76e">
                <BookOpen className="w-10 h-10 text-primary" data-id="element-2033" data-mp="1gq62k0wlt4mv" />
              </div>
              <h3 className="text-2xl font-bold font-heading mb-4 text-primary-dark" data-id="element-2034" data-mp="3f2mriyjj7g4l">
                Information vérifiable
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed" data-id="element-2035" data-mp="co1zrvu4k3g1">
                Une transparence totale sur nos résultats, nos méthodes et nos
                engagements envers nos parties prenantes.
              </p>
            </div>
            <div className="text-center group" data-id="element-2036" data-mp="mlzo0jbzi08n">
              <div className="w-20 h-20 bg-white shadow-md rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-2037" data-mp="hn6aiqwp698h">
                <CheckCircle2 className="w-10 h-10 text-brand-orange" data-id="element-2038" data-mp="3l4eqt6bs1113" />
              </div>
              <h3 className="text-2xl font-bold font-heading mb-4 text-primary-dark" data-id="element-2039" data-mp="3l2k2rj57ksss">
                Certifié Qualiopi
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed" data-id="element-2040" data-mp="3v2avbfa7kxq8">
                Une démarche qualité reconnue par l'État, garantissant le
                sérieux et la conformité de nos actions de formation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Teaser BDE */}
      <section className="py-16 bg-primary-dark text-white relative overflow-hidden" data-id="element-2041" data-mp="1hubdvh3e1lqm">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/4 w-[300px] h-[300px] bg-brand-orange rounded-full blur-[80px] opacity-30 pointer-events-none" data-id="element-2042" data-mp="1mncvhwuzbzdw" />
        <div className="container relative z-10" data-id="element-2043" data-mp="1cw9ronao16ax">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-white/5 p-8 md:p-10 rounded-3xl border border-white/10" data-id="element-2044" data-mp="1cuozv5fqk1gr">
            <div className="flex items-center gap-6" data-id="element-2045" data-mp="25lytv3z43ce4">
              <div className="w-16 h-16 bg-brand-orange rounded-2xl flex items-center justify-center shrink-0" data-id="element-2046" data-mp="25jconazp18z2">
                <Heart className="w-8 h-8 text-white animate-soft-pulse" data-id="element-2047" data-mp="1ro0nakr18k96" />
              </div>
              <div data-id="element-2048" data-mp="1wmlnik6ab3xm">
                <h3 className="font-extrabold font-heading text-2xl mb-2" data-id="element-2049" data-mp="g6sfdph6gm09">
                  BDE Zillenium
                </h3>
                <p className="text-lavande text-lg" data-id="element-2050" data-mp="l6n53pp7v5jq">
                  Une vie étudiante dynamique, conviviale et engagée.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0" data-id="element-2051" data-mp="2hv6gxapki0cv">
              <Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark" asChild data-id="element-2052" data-mp="2hy12j5kbhr7v">
                <Link to="/le-cfa" data-id="element-2053" data-mp="244a1ymokdhoa">En savoir plus</Link>
              </Button>
              <Button className="bg-brand-orange hover:bg-[#E67E22] text-white" asChild data-id="element-2054" data-mp="242727axm0a6d">
                <a href="https://www.instagram.com/bdezillenium/" target="_blank" rel="noopener noreferrer" data-id="element-2055" data-mp="1u5kg6itjrd2r">
                  Suivre sur Instagram
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Nos formations */}
      <section className="py-32 bg-white" data-id="element-2056" data-mp="3kjb89othd5mx">
        <div className="container" data-id="element-2057" data-mp="16b0oztiq7moa">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8" data-id="element-2058" data-mp="169zatg6eyk2c">
            <SectionHeading title="Nos formations en alternance" subtitle="Un diplôme d'État (BTS) et un titre professionnel du Ministère du Travail, tous deux inscrits au RNCP, pour répondre aux besoins réels des entreprises du territoire." className="mb-0 max-w-2xl" data-id="element-2059" data-mp="1b8kc7ixej9sd" />
            <Button size="lg" variant="outline" className="hidden md:flex" asChild data-id="element-2060" data-mp="ndr0hxyjnj06">
              <Link to="/formations" data-id="element-2061" data-mp="wcbe4kp8z2hj">
                Voir toutes les formations{' '}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-2062" data-mp="11c777owep9ib" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-start" data-id="element-2063" data-mp="27sj9o269hdjp">
            {[...FORMATIONS].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0)).map(formation => <div key={formation.id} className={formation.isNew ? 'md:col-span-2' : ''} data-id="element-2064" data-mp="2b8vy3y1r941n">
                  <FormationCard formation={formation} data-id="element-2065" data-mp="26cp14urc72vu" />
                </div>)}
          </div>

          <div className="mt-10 max-w-3xl mx-auto" data-id="element-2066" data-mp="2k80uaxpuglan">
            <Cap360 data-id="element-2067" data-mp="2g7pd5uwox33x" />
          </div>

          <div className="mt-12 text-center md:hidden" data-id="element-2068" data-mp="2gauwetin1arq">
            <Button size="lg" variant="outline" className="w-full" asChild data-id="element-2069" data-mp="2u7hytbtd0t51">
              <Link to="/formations" data-id="element-2070" data-mp="2p9ykyd0kqidm">Voir toutes les formations</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Offres d'alternance */}
      <section className="py-24 bg-white" data-id="element-2071" data-mp="3405t9zwval0l">
        <div className="container max-w-5xl mx-auto" data-id="element-2072" data-mp="2yz00y374czrl">
          <div className="bg-lavande rounded-[2rem] p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8" data-id="element-2073" data-mp="2z1m65w6jf36n">
            <div className="md:flex-1" data-id="element-2074" data-mp="3807vqbaffigx">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3" data-id="element-2075" data-mp="38196sfrg04yn">
                Offres en cours
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-primary-dark mb-4 tracking-tight" data-id="element-2076" data-mp="fykjbpr0k3b">
                {OFFRES.filter(o => o.ouverte).length} entreprises recherchent
                un alternant
              </h2>
              <p className="text-primary-dark/70 text-lg leading-relaxed mb-4" data-id="element-2077" data-mp="3ml29wxphw1ip">
                Postes en vente et relation client dans l'Yonne : prêt-à-porter,
                grande distribution, automobile. Candidature gratuite.
              </p>
              <p className="font-bold text-primary-dark leading-snug" data-id="element-2078" data-mp="2yxc5nbe0s052">
                Démarrage immédiat possible en entreprise — rentrée en formation
                le {RENTREE.dateLabel}.
              </p>
            </div>
            <Button size="lg" className="bg-primary-dark hover:bg-primary text-white text-lg px-8 py-7 shrink-0 group" asChild data-id="element-2079" data-mp="2pxfft6fw8v8f">
              <Link to="/offres" className="flex items-center" data-id="element-2080" data-mp="10fii912u3iwc">
                Voir les offres
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2081" data-mp="voa9865f1zjp" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      <SocialProof data-id="element-2082" data-mp="mp5e79t2coca" />

      <Temoignages data-id="element-2083" data-mp="mlr8psp3rz0m" />

      {/* Qualiopi Teaser */}
      <section className="py-32 bg-lavande relative overflow-hidden" data-id="element-2084" data-mp="1jed0ik1ib1h9">
        <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-white rounded-full blur-[120px] opacity-60 pointer-events-none" data-id="element-2085" data-mp="1ef0axrj93gaz" />
        <div className="container max-w-6xl relative z-10" data-id="element-2086" data-mp="1afj5uz7qkl6c">
          <QualiopiTeaser data-id="element-2087" data-mp="15daaxxbaz91l" />
        </div>
      </section>

      {/* Entreprises Teaser */}
      <section className="py-32 bg-primary-dark text-white relative overflow-hidden" data-id="element-2088" data-mp="3v3xez6vfop5a">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary rounded-full blur-[100px] opacity-40 -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-2089" data-mp="3q41cjc2dx5ru" />
        <div className="container text-center max-w-4xl mx-auto relative z-10" data-id="element-2090" data-mp="25kp6mhqih4sl">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold mb-8 tracking-tight leading-[1.1]" data-id="element-2091" data-mp="2aluykdccko12">
            Entreprises : recrutez vos futurs talents
          </h2>
          <p className="text-lavande text-xl md:text-2xl mb-12 leading-relaxed font-medium" data-id="element-2092" data-mp="2jfhv45odpvbb">
            Confiez-nous vos besoins en recrutement. Nous sourçons,
            présélectionnons et formons les profils adaptés à votre
            développement commercial.
          </p>
          <Button size="lg" className="bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-2093" data-mp="2ycilo5uu3k87">
            <Link to="/entreprises" data-id="element-2094" data-mp="13xocyb513rlr">Découvrir notre offre entreprise</Link>
          </Button>
        </div>
      </section>

      {/* FAQ teaser */}
      <section className="py-24 bg-muted" data-id="element-2095" data-mp="17z1kqqrom4vb">
        <div className="container max-w-4xl mx-auto text-center" data-id="element-2096" data-mp="p29h6jah3ef8">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-primary-dark mb-5 tracking-tight" data-id="element-2097" data-mp="p0esctw2z25v">
            Vous vous posez des questions ?
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-2098" data-mp="xtjkh2baqox4">
            Âge limite, coût, salaire d'apprenti, recherche d'entreprise, aides
            pour les employeurs : nous avons rassemblé les réponses aux
            questions qu'on nous pose le plus souvent.
          </p>
          <Button size="lg" variant="outline" className="border-primary/20 text-primary-dark hover:bg-primary/5 text-lg px-8 py-7 group" asChild data-id="element-2099" data-mp="16esgob1gr2o">
            <Link to="/faq" className="flex items-center" data-id="element-2100" data-mp="a7czzgkorgq0">
              Consulter la FAQ
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2101" data-mp="1qhnqjoxs540c" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Contact direct */}
      <section className="py-24 bg-white" data-id="element-2102" data-mp="1tzv0p1cdcqnf">
        <div className="container max-w-6xl mx-auto" data-id="element-2103" data-mp="1y2rmt214b4w4">
          <ContactCTA title="Un doute, une question ? Appelez-nous" subtitle="Formations, financement, recherche d'entreprise : un conseiller vous répond directement, sans engagement. Vous préférez écrire ? Nous sommes aussi sur WhatsApp." action={{
          label: 'Ou déposez votre candidature en ligne',
          to: '/candidater'
        }} data-id="element-2104" data-mp="1y4cg6obp4hse" />
        </div>
      </section>
    </div>;
}